import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  EvidenceLevel,
  FlagSeverity,
  InformationRequestAudience,
  InformationRequestKind,
  InformationRequestListResponse,
  InformationRequestView,
  MemoSectionKey,
  MoneyView,
  ReviewConfidence,
  ReviewResponse,
  ReviewStatus,
  ReviewView,
  WorkQueueResponse,
  WorkQueueScope,
} from "@tex/finance-shared";
import { ApiError, apiGet, apiSend } from "./api";
import { caseKeys } from "./cases";
import { lenderKeys } from "./lenders";

const IDEMPOTENCY_HEADER = "Idempotency-Key";
const cases = "/api/v1/cases";

export const reviewKeys = {
  queue: (scope: WorkQueueScope) => ["work-queue", scope] as const,
  review: (caseId: string) => ["review", caseId] as const,
  requests: (caseId: string) => ["information-requests", caseId] as const,
};

/** Request bodies mirror the zod schemas in apps/api/src/routes/reviews.ts. */
export interface FactInput {
  id?: string;
  field: string;
  value: string | number | boolean | MoneyView | null;
  evidence: EvidenceLevel;
  source: string;
  as_of: string | null;
  note: string | null;
}

export interface ReviewDetailsInput {
  expected_lock_version: number;
  confidence?: ReviewConfidence | null;
  confidence_reasons?: string | null;
  target_program_version_id?: string | null;
  internal_notes?: string | null;
}

export interface CreateInformationRequestInput {
  audience: InformationRequestAudience;
  kind: InformationRequestKind;
  document_type: string | null;
  title: string;
  detail: string;
  due_on: string | null;
}

export type CloseCommand = "accept" | "cancel" | "reopen";

export function useWorkQueue(scope: WorkQueueScope, enabled = true) {
  return useQuery({
    queryKey: reviewKeys.queue(scope),
    enabled,
    queryFn: () =>
      apiGet<WorkQueueResponse>(`/api/v1/work-queue?scope=${scope}`),
  });
}

export function useReview(caseId: string, enabled = true) {
  return useQuery({
    queryKey: reviewKeys.review(caseId),
    enabled,
    queryFn: () => apiGet<ReviewResponse>(`${cases}/${caseId}/review`),
  });
}

export function useInformationRequests(caseId: string, enabled = true) {
  return useQuery({
    queryKey: reviewKeys.requests(caseId),
    enabled,
    queryFn: () =>
      apiGet<InformationRequestListResponse>(
        `${cases}/${caseId}/information-requests`,
      ),
  });
}

export function useAssignCase(caseId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (reviewer_user_id: string | null) =>
      apiSend<unknown>("PUT", `${cases}/${caseId}/assignment`, {
        reviewer_user_id,
      }),
    onSuccess: () =>
      Promise.all([
        qc.invalidateQueries({ queryKey: ["work-queue"] }),
        qc.invalidateQueries({ queryKey: caseKeys.detail(caseId) }),
      ]),
  });
}

/**
 * Review edits return the whole view; a `version_conflict` means our `lock_version` is stale, so
 * the review is refetched and the reviewer sees the current state before retrying.
 */
function useReviewMutation<TVars>(
  caseId: string,
  fn: (vars: TVars) => Promise<ReviewView>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: (review) => {
      qc.setQueryData<ReviewResponse>(reviewKeys.review(caseId), (prev) =>
        prev
          ? {
              ...prev,
              review,
              versions: prev.versions.some((v) => v.id === review.id)
                ? prev.versions.map((v) =>
                    v.id === review.id
                      ? {
                          ...v,
                          status: review.status,
                          frozen_at: review.frozen_at,
                          reviewer_user_id: review.reviewer_user_id,
                        }
                      : v,
                  )
                : [
                    {
                      id: review.id,
                      version: review.version,
                      status: review.status,
                      reviewer_user_id: review.reviewer_user_id,
                      frozen_at: review.frozen_at,
                      created_at: review.created_at,
                    },
                    ...prev.versions,
                  ],
            }
          : prev,
      );
      return Promise.all([
        qc.invalidateQueries({ queryKey: reviewKeys.review(caseId) }),
        qc.invalidateQueries({ queryKey: ["work-queue"] }),
        qc.invalidateQueries({ queryKey: caseKeys.detail(caseId) }),
        qc.invalidateQueries({ queryKey: lenderKeys.routing(caseId) }),
      ]);
    },
    onError: (err) => {
      if (
        err instanceof ApiError &&
        (err.code === "version_conflict" || err.code === "review_frozen")
      ) {
        return qc.invalidateQueries({ queryKey: reviewKeys.review(caseId) });
      }
    },
  });
}

export function useOpenReview(caseId: string, idempotencyKey: string) {
  return useReviewMutation(caseId, () =>
    apiSend<ReviewView>("POST", `${cases}/${caseId}/review`, undefined, {
      [IDEMPOTENCY_HEADER]: idempotencyKey,
    }),
  );
}

export function useSaveMemoSection(caseId: string) {
  return useReviewMutation(
    caseId,
    (body: {
      expected_lock_version: number;
      section: MemoSectionKey;
      body: string;
    }) => apiSend<ReviewView>("PUT", `${cases}/${caseId}/review/memo`, body),
  );
}

export function useReplaceFacts(caseId: string) {
  return useReviewMutation(
    caseId,
    (body: { expected_lock_version: number; facts: FactInput[] }) =>
      apiSend<ReviewView>("PUT", `${cases}/${caseId}/review/facts`, body),
  );
}

export function useUpdateReviewDetails(caseId: string) {
  return useReviewMutation(caseId, (body: ReviewDetailsInput) =>
    apiSend<ReviewView>("PUT", `${cases}/${caseId}/review/details`, body),
  );
}

export function useSetReviewStatus(caseId: string) {
  return useReviewMutation(
    caseId,
    (body: {
      expected_lock_version: number;
      status: ReviewStatus;
      note: string | null;
    }) => apiSend<ReviewView>("PUT", `${cases}/${caseId}/review/status`, body),
  );
}

export function useAddFlag(caseId: string) {
  return useReviewMutation(
    caseId,
    (body: {
      expected_lock_version: number;
      code: string;
      severity: FlagSeverity;
      note: string;
    }) => apiSend<ReviewView>("POST", `${cases}/${caseId}/review/flags`, body),
  );
}

export function useResolveFlag(caseId: string) {
  return useReviewMutation(
    caseId,
    ({
      flagId,
      ...body
    }: {
      flagId: string;
      expected_lock_version: number;
      resolution: string;
    }) =>
      apiSend<ReviewView>(
        "POST",
        `${cases}/${caseId}/review/flags/${flagId}/resolve`,
        body,
      ),
  );
}

export function useFreezeReview(caseId: string, idempotencyKey: string) {
  return useReviewMutation(caseId, (body: { expected_lock_version: number }) =>
    apiSend<ReviewView>("POST", `${cases}/${caseId}/review/freeze`, body, {
      [IDEMPOTENCY_HEADER]: idempotencyKey,
    }),
  );
}

function useRequestMutation<TVars>(
  caseId: string,
  fn: (vars: TVars) => Promise<InformationRequestView>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () =>
      Promise.all([
        qc.invalidateQueries({ queryKey: reviewKeys.requests(caseId) }),
        qc.invalidateQueries({ queryKey: reviewKeys.review(caseId) }),
        qc.invalidateQueries({ queryKey: ["work-queue"] }),
      ]),
  });
}

export function useCreateInformationRequest(
  caseId: string,
  idempotencyKey: string,
) {
  return useRequestMutation(caseId, (body: CreateInformationRequestInput) =>
    apiSend<InformationRequestView>(
      "POST",
      `${cases}/${caseId}/information-requests`,
      body,
      { [IDEMPOTENCY_HEADER]: idempotencyKey },
    ),
  );
}

export function useRespondToInformationRequest(
  caseId: string,
  idempotencyKey: string,
) {
  return useRequestMutation(
    caseId,
    ({
      requestId,
      ...body
    }: {
      requestId: string;
      note: string | null;
      document_id: string | null;
    }) =>
      apiSend<InformationRequestView>(
        "POST",
        `${cases}/${caseId}/information-requests/${requestId}/response`,
        body,
        { [IDEMPOTENCY_HEADER]: idempotencyKey },
      ),
  );
}

export function useCloseInformationRequest(caseId: string) {
  return useRequestMutation(
    caseId,
    ({
      requestId,
      ...body
    }: {
      requestId: string;
      command: CloseCommand;
      note: string | null;
    }) =>
      apiSend<InformationRequestView>(
        "POST",
        `${cases}/${caseId}/information-requests/${requestId}/close`,
        body,
      ),
  );
}
